import { Alert, Button, Empty, Spin, Tag } from "@arco-design/web-react";

import { feeAdjustmentSpecUnit } from "../service";
import { confirmationDecimalInput } from "../service/confirmationDecimalDisplay";
import type { FulfillmentOrderChangeBatch, FulfillmentOrderDetail, FulfillmentOrderLine } from "../types";
import "./fulfillmentOrderLineAdjustmentCards.css";

type Props = {
  line: FulfillmentOrderLine;
  detail: FulfillmentOrderDetail | null;
  currentVersionId?: string | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  changes?: FulfillmentOrderChangeBatch[];
  formatAmount?: (value: string | null | undefined, signed?: boolean) => string;
  /** 正式确认行池与合同抽屉共用记录卡片；兼容调用保留表格。 */
  layout?: "table" | "cards";
};

type AdjustmentRow = {
  id: string;
  sourceType: "确认件增减项";
  type: "新增" | "扣减" | "调整";
  beforeName: string;
  afterName: string;
  beforeAmount: string;
  afterAmount: string;
  deltaAmount: string;
  reason: string;
  createdAt: string;
  revision: string;
  pricing?: { before: PricingSnapshot; after: PricingSnapshot };
};

type PricingSnapshot = {
  name: string;
  quantity: string;
  billingCount: string;
  unit: string;
  specUnit: string;
  unitPriceAmount: string;
  amount: string;
};

const moneyCents = (value: string) => {
  const matched = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (!matched) return 0n;
  const cents = BigInt(matched[2]) * 100n + BigInt((matched[3] ?? "").padEnd(2, "0"));
  return matched[1] === "-" ? -cents : cents;
};

const formatCents = (cents: bigint) => {
  const sign = cents < 0n ? "-" : "";
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
};

const signedMoney = (value: string) => {
  const cents = moneyCents(value);
  const sign = cents > 0n ? "+" : cents < 0n ? "-" : "";
  return `${sign}¥${formatCents(cents < 0n ? -cents : cents)}`;
};

const deltaTone = (value: string) => {
  const cents = moneyCents(value);
  return cents > 0n ? "is-positive" : cents < 0n ? "is-negative" : "is-zero";
};

const adjustmentTypeTone = (row: Pick<AdjustmentRow, "type" | "deltaAmount">) => (
  row.type === "新增" ? "is-positive" : row.type === "扣减" ? "is-negative" : deltaTone(row.deltaAmount)
);

const projectChangeLabel = (row: AdjustmentRow) => `${row.beforeName} → ${row.afterName}`;

const snapshotName = (snapshot: Record<string, unknown>, fallback = "未命名项目") => {
  const value = snapshot.item_name_snapshot ?? snapshot.itemNameSnapshot;
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
};

/** 从结构化增减快照读取计价事实，缺失字段明确显示为空值而不伪造金额。 */
function pricingSnapshot(value: unknown, line: FulfillmentOrderLine): PricingSnapshot {
  const snapshot = value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
  const decimalText = (field: string) => typeof snapshot[field] === "string" ? snapshot[field] as string : "-";
  return {
    name: snapshotName(snapshot, line.itemNameSnapshot),
    quantity: decimalText("quantity"),
    billingCount: decimalText("billing_count"),
    unit: typeof snapshot.unit === "string" ? snapshot.unit : line.unit,
    specUnit: typeof snapshot.spec_unit === "string" ? snapshot.spec_unit : feeAdjustmentSpecUnit(line.resourceType),
    unitPriceAmount: decimalText("unit_price_amount"),
    amount: decimalText("amount"),
  };
}

/** 沿替代行关系回溯当前活动行，构造独立的行级增减项数据。 */
export function buildFulfillmentLineAdjustments(
  line: FulfillmentOrderLine,
  detail: FulfillmentOrderDetail | null,
  changes = detail?.changes ?? [],
): AdjustmentRow[] {
  if (!changes.length) return [];
  const relatedLineIds = new Set([line.id]);
  let changed = true;
  while (changed) {
    changed = false;
    changes.forEach((batch) => batch.items.forEach((item) => {
      if (!relatedLineIds.has(item.sourceLineId ?? "")
        && !relatedLineIds.has(item.replacementLineId ?? "")) return;
      [item.sourceLineId, item.replacementLineId].forEach((id) => {
        if (id && !relatedLineIds.has(id)) {
          relatedLineIds.add(id);
          changed = true;
        }
      });
    }));
  }

  const runningAmountByLineId = new Map<string, bigint>();
  const rows: AdjustmentRow[] = [];
  const chronologicalChanges = [...changes].sort((left, right) => (
    left.createdAt.localeCompare(right.createdAt)
      || left.expectedRevision - right.expectedRevision
      || left.id.localeCompare(right.id)
  ));
  chronologicalChanges.forEach((batch) => {
    const adjusted = batch.items.filter((item) => item.action === "adjust"
      && Boolean(item.sourceLineId && relatedLineIds.has(item.sourceLineId)));
    if (batch.changeType === "structured_adjustment") {
      adjusted.forEach((item) => {
        const before = pricingSnapshot(item.snapshot.before, line);
        const after = pricingSnapshot(item.snapshot.after, line);
        runningAmountByLineId.set(item.sourceLineId!, moneyCents(after.amount));
        rows.push({
          id: `${batch.id}:${item.id}`,
          sourceType: "确认件增减项",
          type: "调整",
          beforeName: before.name,
          afterName: after.name,
          beforeAmount: before.amount,
          afterAmount: after.amount,
          deltaAmount: item.amount,
          reason: batch.reason,
          createdAt: batch.createdAt,
          revision: `${batch.expectedRevision} → ${batch.revisionAfter}`,
          pricing: { before, after },
        });
      });
      return;
    }
    if (adjusted.length) {
      const adjustmentsByLineId = new Map<string, typeof adjusted>();
      adjusted.forEach((item) => {
        const sourceLineId = item.sourceLineId!;
        adjustmentsByLineId.set(sourceLineId, [...(adjustmentsByLineId.get(sourceLineId) ?? []), item]);
      });
      let beforeCents = 0n;
      let deltaCents = 0n;
      adjustmentsByLineId.forEach((items, sourceLineId) => {
        const sourceAmount = items[0]?.snapshot.amount;
        const fallbackCents = typeof sourceAmount === "string" ? moneyCents(sourceAmount) : 0n;
        const lineBeforeCents = runningAmountByLineId.get(sourceLineId) ?? fallbackCents;
        const lineDeltaCents = items.reduce((total, item) => total + moneyCents(item.amount), 0n);
        beforeCents += lineBeforeCents;
        deltaCents += lineDeltaCents;
        runningAmountByLineId.set(sourceLineId, lineBeforeCents + lineDeltaCents);
      });
      const name = snapshotName(adjusted[0]!.snapshot);
      rows.push({
        id: batch.id,
        sourceType: "确认件增减项",
        type: "调整",
        beforeName: name,
        afterName: name,
        beforeAmount: formatCents(beforeCents),
        afterAmount: formatCents(beforeCents + deltaCents),
        deltaAmount: formatCents(deltaCents),
        reason: batch.reason,
        createdAt: batch.createdAt,
        revision: `${batch.expectedRevision} → ${batch.revisionAfter}`,
      });
      return;
    }
    const deleted = batch.items.filter((item) => item.action === "delete"
      && Boolean(item.sourceLineId && relatedLineIds.has(item.sourceLineId)));
    const added = batch.items.filter((item) => item.action === "add"
      && Boolean(item.replacementLineId && relatedLineIds.has(item.replacementLineId)));
    if (!deleted.length && !added.length) return;
    const beforeCents = deleted.reduce((total, item) => total + moneyCents(item.amount), 0n);
    const afterCents = added.reduce((total, item) => total + moneyCents(item.amount), 0n);
    rows.push({
      id: batch.id,
      sourceType: "确认件增减项",
      type: !deleted.length ? "新增" : !added.length ? "扣减" : "调整",
      beforeName: deleted[0] ? snapshotName(deleted[0].snapshot) : "-",
      afterName: added[0] ? snapshotName(added[0].snapshot) : "-",
      beforeAmount: formatCents(beforeCents),
      afterAmount: formatCents(afterCents),
      deltaAmount: formatCents(afterCents - beforeCents),
      reason: batch.reason,
      createdAt: batch.createdAt,
      revision: `${batch.expectedRevision} → ${batch.revisionAfter}`,
    });
  });
  return rows.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

/** 确认行下方的独立增减记录，支持紧凑卡片与兼容表格。 */
export function FulfillmentOrderLineAdjustmentsPanel({ line, detail, currentVersionId, loading, error, onRetry, changes, formatAmount, layout = "table" }: Props) {
  const decimal = formatAmount ? confirmationDecimalInput : (value: string) => value;
  const money = formatAmount ? (value: string) => formatAmount(value, false) : (value: string) => `¥${value}`;
  const allChanges = changes ?? detail?.changes ?? [];
  const versionChanges = currentVersionId
    ? allChanges.filter((batch) => !batch.baseVersionId || batch.baseVersionId === currentVersionId)
    : allChanges;
  const rows = buildFulfillmentLineAdjustments(line, detail, versionChanges);
  return (
    <section role="region" aria-label={`${line.itemNameSnapshot}增减项`} className={`fulfillment-order-line-adjustments${layout === "cards" ? " fulfillment-order-line-adjustments--cards" : ""}`}>
      {loading ? <div className="fulfillment-order-line-adjustments__loading"><Spin dot /></div> : null}
      {!loading && error ? <Alert type="error" showIcon content="增减项加载失败，请稍后重试。" action={<Button size="small" onClick={onRetry}>重试</Button>} /> : null}
      {!loading && !error && !rows.length ? <Empty description="该项目暂无增减项" /> : null}
      {!loading && !error && rows.length && layout === "cards" ? (
        <div className="fulfillment-order-line-adjustment-cards">
          {rows.map(row => {
            const valueChanged = (before: string, after: string) => confirmationDecimalInput(before) !== confirmationDecimalInput(after);
            const metrics = [
              ...(row.pricing ? [
                { label: "数量", before: `${decimal(row.pricing.before.quantity)} ${row.pricing.before.unit}`, after: `${decimal(row.pricing.after.quantity)} ${row.pricing.after.unit}`, changed: valueChanged(row.pricing.before.quantity, row.pricing.after.quantity) || row.pricing.before.unit !== row.pricing.after.unit },
                { label: "规格", before: `${decimal(row.pricing.before.billingCount)} ${row.pricing.before.specUnit}`, after: `${decimal(row.pricing.after.billingCount)} ${row.pricing.after.specUnit}`, changed: valueChanged(row.pricing.before.billingCount, row.pricing.after.billingCount) || row.pricing.before.specUnit !== row.pricing.after.specUnit },
                { label: "单价", before: money(row.pricing.before.unitPriceAmount), after: money(row.pricing.after.unitPriceAmount), changed: valueChanged(row.pricing.before.unitPriceAmount, row.pricing.after.unitPriceAmount) },
              ] : []),
              { label: "总额", before: money(row.beforeAmount), after: money(row.afterAmount), changed: valueChanged(row.beforeAmount, row.afterAmount) },
            ];
            return <article key={row.id} className="fulfillment-order-line-adjustment-card" aria-label={`${row.type}记录`}>
              <header className="fulfillment-order-line-adjustment-card__header">
                <div className="fulfillment-order-line-adjustment-card__identity">
                  <div className="fulfillment-order-line-adjustment-card__tags"><Tag color="arcoblue">{row.type}</Tag><span>{row.sourceType}</span></div>
                  <div className="fulfillment-order-line-adjustment-card__meta"><time dateTime={row.createdAt}>{new Date(row.createdAt).toLocaleString("zh-CN", { hour12: false })}</time><span>修订 {row.revision}</span></div>
                </div>
                <div className="fulfillment-order-line-adjustment-card__amount"><span>本次增减</span><strong className={`fulfillment-order-line-adjustment-card__delta ${deltaTone(row.deltaAmount)}`}>{formatAmount ? formatAmount(row.deltaAmount, true) : signedMoney(row.deltaAmount)}</strong></div>
              </header>
              <dl className="fulfillment-order-line-adjustment-card__metrics">
                <div className="fulfillment-order-line-adjustment-card__project">
                  <dt>项目变化</dt>
                  <dd><strong>{row.beforeName === row.afterName ? row.afterName : projectChangeLabel(row)}</strong></dd>
                </div>
                {metrics.map(metric => <div key={metric.label} role="group" aria-label={`${metric.label}变化`}>
                  <dt>{metric.label}</dt>
                  <dd><span className="fulfillment-order-line-adjustment-card__before">{metric.before}</span><span className="fulfillment-order-line-adjustment-card__arrow" aria-label="调整为">→</span>{metric.changed ? <strong>{metric.after}</strong> : <span>{metric.after}</span>}</dd>
                </div>)}
              </dl>
              <div className="fulfillment-order-line-adjustment-card__reason"><span>调整原因</span><p>{row.reason || "—"}</p></div>
            </article>;
          })}
        </div>
      ) : null}
      {!loading && !error && rows.length && layout === "table" ? (
        <div className="fulfillment-order-line-adjustments__scroll">
          <table aria-label={`${line.itemNameSnapshot}增减项明细`}>
            <thead><tr><th>类型</th><th>增减类型</th><th>增减金额</th><th>项目变化</th><th>原因</th><th>时间 / 修订</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id}>
                <td><span className="fulfillment-order-line-adjustments__source is-ordinary">{row.sourceType}</span></td>
                <td><span className={`fulfillment-order-line-adjustments__type is-${row.type === "新增" ? "add" : row.type === "扣减" ? "deduct" : "change"} ${adjustmentTypeTone(row)}`}>{row.type}</span></td>
                <td className={`money-value fulfillment-order-line-adjustments__delta fulfillment-order-line-adjustments__amount-cell ${deltaTone(row.deltaAmount)}`}>
                  <span className={`fulfillment-order-line-adjustments__delta-value ${deltaTone(row.deltaAmount)}`}>{formatAmount ? formatAmount(row.deltaAmount, true) : signedMoney(row.deltaAmount)}</span>
                </td>
                <td>{row.pricing ? (
                  <>
                    <span>{row.beforeName === row.afterName ? row.afterName : projectChangeLabel(row)}</span>
                    <small>数量 {decimal(row.pricing.before.quantity)} {row.pricing.before.unit} → {decimal(row.pricing.after.quantity)} {row.pricing.after.unit}</small>
                    <small>规格 {decimal(row.pricing.before.billingCount)} {row.pricing.before.specUnit} → {decimal(row.pricing.after.billingCount)} {row.pricing.after.specUnit}</small>
                    <small>单价 {money(row.pricing.before.unitPriceAmount)} → {money(row.pricing.after.unitPriceAmount)}</small>
                    <small>总额 {money(row.beforeAmount)} → {money(row.afterAmount)}</small>
                  </>
                ) : projectChangeLabel(row)}</td>
                <td>{row.reason}</td>
                <td>{new Date(row.createdAt).toLocaleString("zh-CN", { hour12: false })}<small>修订 {row.revision}</small></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
